import "zod/compile";
import { z } from "zod";

/**
 * A Validation's severity: an `error` that fails marks the Run's Validation
 * as failed and its Outputs as failing validation; a `warning` only reports.
 */
export const workbookValidationSeveritySchema = z.enum(["warning", "error"]);
export type WorkbookValidationSeverity = z.infer<
  typeof workbookValidationSeveritySchema
>;

/** The kinds of Validation, in the order the UI offers them. */
export const WORKBOOK_VALIDATION_KINDS = [
  "not-empty",
  "unique",
  "accepted-values",
  "range",
  "pattern",
  "no-errors",
  "formula",
  "in-sheet",
] as const;
export type WorkbookValidationKind = (typeof WORKBOOK_VALIDATION_KINDS)[number];

const rule = {
  /** Stable, so a Run's check of a Validation is found again after edits. */
  id: z.uuid(),
  /** The Worksheet column it checks (for a Formula, the one it is shown on). */
  column: z.string().min(1),
  severity: workbookValidationSeveritySchema.default("error"),
};

/**
 * A Validation: a rule every row of a Worksheet's Result must pass, checked
 * over the whole Result by every Run — never only the rows on screen. A cell
 * holding an error value fails every kind; an Empty cell passes every kind
 * but Not Empty (as a spreadsheet's "Ignore blank"), so rules compose.
 * - `not-empty`: no Empty cells.
 * - `unique`: no value appears twice (Empty cells aside).
 * - `accepted-values`: every value is one of `values`, each read as the
 *   column's Declared type, as a Value list filter reads its values.
 * - `range`: every value is at least `min` and at most `max` (a Number or
 *   Date column; either bound may be absent).
 * - `pattern`: every value, as text, contains a match of the regular
 *   expression `pattern` (RE2 syntax; anchor it with ^ and $ to match whole).
 * - `no-errors`: no error values.
 * - `formula`: a TRUE/FALSE Formula over the row's columns is TRUE; FALSE or
 *   an error value fails, Empty passes.
 * - `in-sheet`: every value appears in another Worksheet's `matchColumn`
 *   (`exactlyOnce`: exactly once), compared as text: no row lost between
 *   the two, or each row matched once. It relates Worksheets, never fixed
 *   numbers, so it stays true period to period; a warning unless set to
 *   an error. Checked after the transform, against the other Worksheet's
 *   Result in the same Run or, for a full Run, its Current Result.
 */
export const workbookValidationSchema = z.discriminatedUnion("kind", [
  z.object({ ...rule, kind: z.literal("not-empty") }),
  z.object({ ...rule, kind: z.literal("unique") }),
  z.object({
    ...rule,
    kind: z.literal("accepted-values"),
    values: z.array(z.string().max(1000)).max(1000).default([]),
  }),
  z.object({
    ...rule,
    kind: z.literal("range"),
    min: z.string().trim().max(100).optional(),
    max: z.string().trim().max(100).optional(),
  }),
  z.object({
    ...rule,
    kind: z.literal("pattern"),
    pattern: z.string().max(500),
  }),
  z.object({ ...rule, kind: z.literal("no-errors") }),
  z.object({
    ...rule,
    kind: z.literal("formula"),
    expression: z.string().min(1).max(2000),
  }),
  z.object({
    ...rule,
    kind: z.literal("in-sheet"),
    severity: workbookValidationSeveritySchema.default("warning"),
    sheetId: z.uuid(),
    matchColumn: z.string().min(1),
    exactlyOnce: z.boolean().default(false),
  }),
]);
export type WorkbookValidation = z.infer<typeof workbookValidationSchema>;

/** The most Validations one Worksheet holds. */
export const WORKBOOK_MAX_VALIDATIONS = 50;
/** The most failing rows a Run keeps per Validation, to show and to filter the grid to. */
export const WORKBOOK_VALIDATION_SAMPLE_ROWS = 100;

/**
 * How a grid samples a Large table (past the inline row limit), whose rows it
 * can't show all of: its first rows (the default), its last rows, a seeded
 * random sample (the same seed, the same rows), a random sample stratified by
 * a column (each value's rows in proportion, every value represented while
 * the sample has room), or the most recent rows by a Date column. A sample is
 * a view: Validations, Filter counts and Outputs always read every row.
 */
export const workbookSampleSchema = z.discriminatedUnion("method", [
  z.object({ method: z.literal("first") }),
  z.object({ method: z.literal("last") }),
  z.object({
    method: z.literal("random"),
    seed: z.number().int().min(0).max(2_147_483_647).default(1),
  }),
  z.object({
    method: z.literal("stratified"),
    column: z.string().min(1),
    seed: z.number().int().min(0).max(2_147_483_647).default(1),
  }),
  z.object({ method: z.literal("recent"), column: z.string().min(1) }),
]);
export type WorkbookSample = z.infer<typeof workbookSampleSchema>;
export type WorkbookSampleMethod = WorkbookSample["method"];
export const WORKBOOK_SAMPLE_METHODS = [
  "first",
  "last",
  "random",
  "stratified",
  "recent",
] as const satisfies readonly WorkbookSampleMethod[];
